import { Route2g, RouteStop, Toll } from "@/reducers/routes2gSlice"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import { OrderAnalyzeRoute } from "@/lib/orderAnalyzeRouteParser"
import RouteStopList, { RouteStopDiff } from "@/components/RouteStopList"
import { Button } from "@/components/ui/button"
import { AlertCircleIcon, ArrowLeft, Check, Copy, Share2 } from "lucide-react"
import CollapsibleVehicleTollInfo from "@/components/maps/CollapsibleVehicleTollInfo"
import * as React from "react"
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { APIProvider } from "@vis.gl/react-google-maps"
import AdHocCommands, {
  AvoidType,
  CarRouteConfig,
  LoadType,
} from "@/api/adHocCommands"
import { RouteInfo } from "@/components/maps/RouteView"
import { Spinner } from "@blueprintjs/core"
import AvoidSelect from "@/components/transportOrder/AvoidSelect"
import LoadTypeSelect from "@/components/transportOrder/LoadTypeSelect"
import { ScrollAreaWithShadow } from "@/components/ui/scroll-area"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import { Alert, AlertDescription } from "../ui/alert"
import { AlertTitle } from "@/components/ui/alert"
import MapView from "../maps/MapView"
import VehicleTypeSelect, {
  carConfigs,
  VEHICLE_TYPE_NAMES,
  VehicleType,
} from "./VehicleTypeSelect"
import { generateRouteName, saveRoute } from "@/lib/saveRoutes"
import { SavedRoutesSelector } from "@/components/ui/saved-routes-selector"
import { ClearRouteDialog } from "@/components/ui/clear-route-dialog"
import ClipboardWriter from "@/lib/clipboardWriter"
import { toast } from "sonner"
import { generateShareUrl, saveRouteState } from "@/lib/routeStateUtils"
import { getOptionsSummaryText } from "@/components/maps/RouteOptionsSelect"
import { useSelector } from "react-redux"
import {
  selectIsHereRouting,
  selectRoutingProvider,
} from "@/reducers/serviceConfigSelectors"
import { generateRouteMessage } from "@/components/analyzeOrderOnPage/utils"
import DriverSelect, {
  SelectedDriverLocation,
} from "@/components/analyzeOrderOnPage/DriverSelect"
import XmppPubSubApi from "@/api/xmppPubSubApi"
import { XmppContext } from "@/stream/xmppClient"
import { useNaviConfig } from "@/hooks/useNaviConfig"
import PlanRouteParser, {
  AlternativeRoute,
  RouteStopWithHandle,
} from "@/lib/planRouteParser"
import AlternativeLeg from "@/components/maps/AlternativeLeg"
import AlternativeRouteSelector from "@/components/maps/AlternativeRouteSelector"
import HoveredRouteHighlight from "@/components/maps/HoveredRouteHighlight"
import { useFeatureFlagEnabled } from "posthog-js/react"
import {
  clearRouteHandlesFromPassthroughStops,
  fetchCustomRoute,
  handleFetchPerLegAlternatives,
  PerLegAlternatives,
  restorePassthroughs,
} from "@/hooks/useTemporaryRoute"
import SingleStopMarker from "@/components/maps/mapLibre/SingleStopMarker"

type AnalyzeOrderMapProps = {
  routeData?: OrderAnalyzeRoute
  isLoading?: boolean
  isFailed?: boolean
  onRetryProcessing?: () => void
  onHideModal?: () => void
  onShowModal?: () => void
  onClose?: (force?: boolean) => void
  isHidden?: boolean
  isRetrying?: boolean
  onBackToVerify: (routeData: OrderAnalyzeRoute) => void
  onFetchPolylines: (
    stops: RouteStop[],
    avoidOptions: AvoidType[],
    loadType: LoadType[],
    carConfig: CarRouteConfig,
  ) => Promise<
    | [RouteInfo | undefined, RouteStop[], AlternativeRoute[] | undefined]
    | undefined
  >
  // Props for sharing functionality
  onLoadSavedRoute?: (routeId: string) => void
  extractedData?: any
  fileName?: string
  fileUrl?: string
  sharedRouteState?: any
  onReset?: () => void
  initialAvoidOptions?: AvoidType[]
  initialLoadType?: LoadType[]
  initialVehicleType?: VehicleType
  initialDriver?: SelectedDriverLocation | undefined
  // Marcos/Nawigator transport order created at the verify step; shown as a
  // confirmation banner instead of the copy-only ending.
  naviTransportId?: number
}

export type LangTemplate = {
  greeting: string
  newOrderIntro: string
  confirmation: string
  weight: string
  refNumber: string
  notes: string
  pickup: string
  delivery: string
  stop: string
  customs: string
  pallets: string
  palletsCount: string
}

const AnalyzeOrderOnPageMap: React.FC<AnalyzeOrderMapProps> = ({
  routeData,
  isLoading = false,
  isFailed = false,
  isHidden = false,
  onBackToVerify,
  onClose,
  onFetchPolylines,
  sharedRouteState,
  fileName,
  fileUrl,
  onLoadSavedRoute,
  onReset,
  initialVehicleType,
  initialAvoidOptions,
  initialLoadType,
  initialDriver,
  naviTransportId,
}: AnalyzeOrderMapProps) => {
  const { t } = useTranslation()
  const [messageText, setMessageText] = useState<string | undefined>("")
  const [copySuccessAnimate, setCopySuccessAnimate] = useState(false)
  const [shareSuccessAnimate, setShareSuccessAnimate] = useState(false)
  const [isGeneratingShareLink, setIsGeneratingShareLink] = useState(false)
  const [selectedDriver, setSelectedDriver] = useState<
    SelectedDriverLocation | undefined
  >(initialDriver)

  const [temporaryRoute, setTemporaryRoute] = useState<Route2g | undefined>(
    routeData
      ? {
          stops: routeData.stops,
          jid: routeData.jid,
          lastUpdatedBy: routeData.lastUpdatedBy,
          tolls: routeData.route?.tolls,
        }
      : undefined,
  )
  const [temporaryRouteInfo, setTemporaryRouteInfo] = useState<
    RouteInfo | undefined
  >(
    routeData
      ? {
          ...routeData.route,
        }
      : undefined,
  )
  const [selectedAvoidOptions, setSelectedAvoidOptions] = useState<AvoidType[]>(
    initialAvoidOptions ?? [],
  )
  const [selectedLoadType, setSelectedLoadType] = useState<LoadType[]>(
    initialLoadType ?? [],
  )

  const [selectedVehicleType, setSelectedVehicleType] = useState<VehicleType>(
    initialVehicleType ?? "tir",
  )
  const [isFetchingPolylines, setIsFetchingPolylines] = useState(false)

  const [driverCarConfig, setDriverCarConfig] = useState<
    CarRouteConfig | undefined
  >(undefined)

  // Alternative routes state
  const [alternativeRoutes, setAlternativeRoutes] = useState<
    AlternativeRoute[] | undefined
  >(undefined)
  const [perLegAlternatives, setPerLegAlternatives] = useState<
    PerLegAlternatives[] | undefined
  >(undefined)
  const [hoveredRouteId, setHoveredRouteId] = useState<string | undefined>(
    undefined,
  )

  // Drag state - tracks when user is dragging the route line to add passthrough
  const [isDraggingRoute, setIsDraggingRoute] = useState(false)

  const alternativeRoutesEnabled =
    Boolean(useFeatureFlagEnabled("alternative-routes")) || window.anonymousXMPP
  const hideDistanceLine = useFeatureFlagEnabled(
    "hide-distance-line-in-route-message",
  )

  const { client } = useContext(XmppContext)
  // Marcos/Nawigator mode: the order was already created in the TMS at the
  // verify step, so the ending is a confirmation (plus copy for convenience)
  // instead of the share/copy-only flow.
  const { configured: isMarcosMode } = useNaviConfig(client?.jid?.domain)

  const getCurrentCarConfig = useCallback((): CarRouteConfig => {
    // Use driver's car config if available (either from selected driver or from shared route)
    if (driverCarConfig) {
      return driverCarConfig
    }
    return carConfigs[selectedVehicleType]
  }, [driverCarConfig, selectedVehicleType])

  // Check if we should use driver's config
  const isUsingDriverConfig = Boolean(driverCarConfig)

  const haveFirstStopAsDriverLocation =
    temporaryRoute &&
    temporaryRoute.stops &&
    temporaryRoute.stops.length > 0 &&
    temporaryRoute.stops[0].action === "from_driver_location"

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [isContentReady, setIsContentReady] = useState<boolean>(false)

  // Initialize state from shared route data
  useEffect(() => {
    if (sharedRouteState) {
      setTemporaryRoute(sharedRouteState.temporaryRoute)
      setTemporaryRouteInfo(sharedRouteState.temporaryRouteInfo)
      setSelectedAvoidOptions(sharedRouteState.selectedAvoidOptions || [])
      setSelectedLoadType(sharedRouteState.selectedLoadType || [])
      setSelectedVehicleType(sharedRouteState.selectedVehicleType || "tir")
      setDriverCarConfig(sharedRouteState.driverCarConfig)
      setMessageText(sharedRouteState.messageText || "")
      setIsContentReady(true) // Mark content as ready when shared route is loaded

      // Restore alternatives if available
      if (sharedRouteState.alternativeRoutes) {
        setAlternativeRoutes(sharedRouteState.alternativeRoutes)
      }
      if (sharedRouteState.perLegAlternatives) {
        setPerLegAlternatives(sharedRouteState.perLegAlternatives)
      }
    }
  }, [sharedRouteState])

  // Fetch alternatives for old routes that don't have them saved
  useEffect(() => {
    if (!sharedRouteState || !alternativeRoutesEnabled) return
    if (sharedRouteState.alternativeRoutes) return // Already has alternatives

    const hasStops =
      sharedRouteState.temporaryRoute?.stops &&
      sharedRouteState.temporaryRoute.stops.length >= 2

    if (!hasStops || !window.XMPP || !client) return

    const vehicleType: VehicleType =
      sharedRouteState.selectedVehicleType || "tir"
    const carConfig =
      sharedRouteState.driverCarConfig || carConfigs[vehicleType]

    // Fetch alternatives for the saved route
    onFetchPolylines(
      sharedRouteState.temporaryRoute.stops,
      sharedRouteState.selectedAvoidOptions || [],
      sharedRouteState.selectedLoadType || [],
      carConfig,
    ).then((result) => {
      if (result) {
        const [, stops, alternatives] = result
        if (alternatives && alternatives.length > 0) {
          setAlternativeRoutes(alternatives)

          // Auto-select the first alternative so it appears selected in the UI
          const firstStop = sharedRouteState.temporaryRoute.stops[0]
          const defaultAlt = alternatives[0]
          if (defaultAlt && firstStop) {
            setTemporaryRoute((prev) =>
              prev
                ? {
                    ...prev,
                    stops: [firstStop, ...defaultAlt.stops],
                    routeId: defaultAlt.type,
                    tolls: defaultAlt.tolls || [],
                  }
                : prev,
            )
            setTemporaryRouteInfo(defaultAlt.routeInfo)
          }

          // Fetch per-leg alternatives
          handleFetchPerLegAlternatives(
            stops,
            `on-page@${window.XMPP!.options.domain}`,
            {
              avoid: sharedRouteState.selectedAvoidOptions || [],
              loadType: sharedRouteState.selectedLoadType || [],
              carConfig,
            },
          ).then((perLegResults) => {
            setPerLegAlternatives(perLegResults)
          })
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedRouteState, alternativeRoutesEnabled, client])

  const copyMessage = (e: React.MouseEvent) => {
    e.stopPropagation()
    ClipboardWriter.executeAndWriteResult(async () => messageText || "")
    window.analytics?.track(
      window.anonymousXMPP
        ? "Anonymous_MessageCopied"
        : "OrderOnPage_MessageCopied",
      {
        messageText,
      },
    )

    setCopySuccessAnimate(true)
    setTimeout(() => {
      setCopySuccessAnimate(false)
    }, 700)
  }

  const saveAndGenerateShareUrl = useCallback(async () => {
    setIsGeneratingShareLink(true)
    try {
      const stateId = await saveRouteState({
        // @ts-ignore
        temporaryRoute: {
          ...temporaryRoute,
          stops:
            temporaryRoute?.stops?.map((stop) => ({
              ...stop,
              action:
                stop.action === "from_driver_location" ? "other" : stop.action,
              by: undefined, // Remove the by field
            })) || [],
        },
        temporaryRouteInfo,
        selectedAvoidOptions,
        selectedLoadType,
        selectedVehicleType,
        driverCarConfig: isUsingDriverConfig ? driverCarConfig : undefined,
        messageText,
        fileName,
        fileUrl,
        extractedData: routeData?.routeMetaData,
      })

      const shareUrl = generateShareUrl(stateId)

      window.analytics?.track(
        window.anonymousXMPP
          ? "Anonymous_RouteShared"
          : "OrderOnPage_RouteShared",
        {
          stateId,
          shareUrl,
          routeStopsCount: temporaryRoute?.stops?.length || 0,
        },
      )

      setShareSuccessAnimate(true)
      setTimeout(() => {
        setShareSuccessAnimate(false)
      }, 700)
      toast(t("planOrder.routeToasts.shareLinkCopied"))
      return shareUrl
    } catch (error) {
      console.error("Failed to share route:", error)
      toast.error(t("planOrder.routeToasts.shareLinkFailed"))
    } finally {
      setIsGeneratingShareLink(false)
    }
  }, [
    driverCarConfig,
    fileName,
    fileUrl,
    isUsingDriverConfig,
    messageText,
    routeData?.routeMetaData,
    selectedAvoidOptions,
    selectedLoadType,
    selectedVehicleType,
    temporaryRoute,
    temporaryRouteInfo,
    t,
  ])

  const shareRoute = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      ClipboardWriter.executeAndWriteResult(saveAndGenerateShareUrl)
    },
    [saveAndGenerateShareUrl],
  )

  const fetchNewTemporaryRoute = useCallback(
    async ({
      updatedRoute,
      avoidOptions,
      loadType,
      carConfig,
      passthroughStops,
    }: {
      updatedRoute: Route2g
      avoidOptions: AvoidType[]
      loadType: LoadType[]
      carConfig: CarRouteConfig
      // The same stops with the dispatcher's passthroughs, which
      // `updatedRoute` leaves out so the alternatives start clean. The route
      // through them is then selected as the Custom route.
      passthroughStops?: RouteStop[]
    }) => {
      setIsFetchingPolylines(true)
      try {
        const [routeData, stops, alternatives] =
          (await onFetchPolylines(
            updatedRoute.stops,
            avoidOptions,
            loadType,
            carConfig,
          )) || []
        if (routeData) {
          const throughPassthroughs =
            passthroughStops && stops
              ? restorePassthroughs(passthroughStops, stops)
              : undefined
          // Should it fail, the route stays without the passthroughs.
          const customRoute = throughPassthroughs
            ? await fetchCustomRoute(
                throughPassthroughs,
                `on-page@${window.XMPP!.options.domain}`,
                { ...carConfig, avoid: avoidOptions, loadType },
              ).catch((e) => {
                console.error(e)
                return undefined
              })
            : undefined
          setTemporaryRoute({
            ...updatedRoute,
            stops: customRoute?.stops ?? stops ?? updatedRoute.stops,
            tolls:
              (customRoute
                ? customRoute.route.tolls
                : routeData.routes?.[0]?.tolls) || [],
          })
          setTemporaryRouteInfo(customRoute?.route.routeInfo ?? routeData)
          if (alternativeRoutesEnabled && alternatives) {
            setAlternativeRoutes(
              customRoute ? [customRoute.route, ...alternatives] : alternatives,
            )
            // Fetch per-leg alternatives
            if (stops && stops.length >= 2) {
              const perLegResults = await handleFetchPerLegAlternatives(
                stops,
                `on-page@${window.XMPP!.options.domain}`,
                { avoid: avoidOptions, loadType, carConfig },
              )
              setPerLegAlternatives(perLegResults)
            }
          }
        } else if (stops) {
          setTemporaryRoute({
            ...updatedRoute,
            stops: stops,
            tolls: [],
          })
          setTemporaryRouteInfo(undefined)
          setAlternativeRoutes(undefined)
          setPerLegAlternatives(undefined)
        }
      } catch (e) {
        window.mapToaster?.show({
          message: t("planOrder.routeToasts.fetchFailed"),
          intent: "danger",
          timeout: 3000,
        })
        console.error(e)
        window.analytics?.track("Anonymous_RouteCalculationFailed", {
          error: e instanceof Error ? e.message : String(e),
          stopCount: updatedRoute.stops.length,
          source: "document",
        })
      } finally {
        setIsFetchingPolylines(false)
      }
    },
    [onFetchPolylines, alternativeRoutesEnabled, t],
  )

  const handleTemporaryRouteUpdate = useCallback(
    async (updatedRoute: Route2g, diff: RouteStopDiff) => {
      // We don't have current location so we need to have at least 2 stops
      if (updatedRoute.stops.length === 0) {
        setMessageText("")
        setTemporaryRoute(undefined)
        setTemporaryRouteInfo(undefined)
        setSelectedDriver(undefined)
        setAlternativeRoutes(undefined)
        setPerLegAlternatives(undefined)
        return
      }

      // Only 1 stop - can't have a route, clear route info but keep the stop
      if (updatedRoute.stops.length === 1) {
        updatedRoute.stops[0].is_temporary = true
        setTemporaryRoute(updatedRoute)
        setTemporaryRouteInfo(undefined)
        setAlternativeRoutes(undefined)
        setPerLegAlternatives(undefined)
        // Update driver selection if first stop changed
        if (selectedDriver) {
          const firstStop = updatedRoute.stops[0]
          if (firstStop.action !== "from_driver_location") {
            setSelectedDriver(undefined)
          }
        }
        setMessageText(
          generateRouteMessage({
            route: updatedRoute,
            hideDistanceLine,
          }),
        )
        return
      }

      // Only action changed (not location) - update route and message without fetching
      if (
        diff.hasActionOnlyChanges &&
        !diff.hasNonPassthroughChanges &&
        !diff.hasPassthroughChanges
      ) {
        setTemporaryRoute(updatedRoute)
        setMessageText(
          generateRouteMessage({
            route: updatedRoute,
            hideDistanceLine,
          }),
        )
        return
      }

      const carConfig = getCurrentCarConfig()

      // Check if we have non-passthrough changes WITHOUT passthrough changes
      // This means: stops added/removed/reordered, or avoid/loadType changed, but no passthrough modifications
      const hasNonPassthroughChangesOnly =
        (diff.hasNonPassthroughChanges ||
          diff.hasAvoidChanges ||
          diff.hasLoadTypeChanges ||
          diff.hasOrderChanges) &&
        !diff.hasPassthroughChanges

      if (hasNonPassthroughChangesOnly) {
        if (diff.hasOrderChanges) {
          window.analytics?.track("Anonymous_StopReordered", {
            stopCount: updatedRoute.stops.length,
            source: "document",
          })
        }
        // Branch 1: Non-passthrough changes - reset and fetch fresh alternatives
        // Clear route handles and leave passthroughs out for a clean slate;
        // the passthroughs come back as the Custom route, as in Branch 2
        setAlternativeRoutes(undefined)
        setPerLegAlternatives(undefined)

        const resetStops = updatedRoute.stops.map((stop) => ({
          ...stop,
          route_handle: undefined,
          addressUpdated: true,
        }))
        const sanitizedStops = resetStops.filter(
          (stop) => stop.action !== "passthrough",
        )

        if (sanitizedStops.length === 0) {
          setMessageText("")
          setTemporaryRoute(undefined)
          setTemporaryRouteInfo(undefined)
          return
        }

        // Use fetchNewTemporaryRoute with sanitized stops
        await fetchNewTemporaryRoute({
          updatedRoute: { ...updatedRoute, stops: sanitizedStops },
          avoidOptions: selectedAvoidOptions,
          loadType: selectedLoadType,
          carConfig,
          passthroughStops: resetStops,
        })
      } else if (diff.hasPassthroughChanges && alternativeRoutesEnabled) {
        // Branch 2: Has passthrough changes - create custom route while preserving existing alternatives
        setIsFetchingPolylines(true)
        try {
          let sanitizedStops = updatedRoute.stops
          if (diff.modified.length > 0 || diff.added.length > 0) {
            sanitizedStops = clearRouteHandlesFromPassthroughStops(
              updatedRoute.stops,
            )
          } else if (diff.removed.length > 0 && temporaryRoute) {
            const stopAfterRemovedStop =
              temporaryRoute.stops[diff.removed[0].index + 1]
            sanitizedStops = sanitizedStops.map((stop) => ({
              ...stop,
              route_handle:
                stop.id === stopAfterRemovedStop?.id
                  ? undefined
                  : (stop as RouteStopWithHandle).route_handle,
            }))
          }

          const result = await AdHocCommands.getRoutePlan(
            window.XMPP!,
            `on-page@${window.XMPP!.options.domain}`,
            sanitizedStops,
            {
              ...carConfig,
              avoid: selectedAvoidOptions,
              loadType: selectedLoadType,
            },
          )

          const parsedRoute = PlanRouteParser.parse(result)
          if (parsedRoute) {
            const { stops, alternativeRoutes: newAlternatives } = parsedRoute

            setTemporaryRoute({
              ...updatedRoute,
              stops: [sanitizedStops[0], ...stops],
              tolls: newAlternatives?.[0]?.tolls || [],
            })

            if (newAlternatives?.[0]) {
              const customRoute = newAlternatives[0]
              customRoute.type = "Custom"
              customRoute.routeInfo.routes[0].isCustom = true
              customRoute.routeInfo.routes[0].id = "Custom"
              customRoute.routeInfo.routes[0].label = "Custom"

              const currentAlternativesWithoutCustom = (
                alternativeRoutes || []
              ).filter((alt) => !alt.routeInfo.routes[0].isCustom)

              setAlternativeRoutes([
                customRoute,
                ...currentAlternativesWithoutCustom,
              ])
              setTemporaryRouteInfo(customRoute.routeInfo)
            } else if (parsedRoute.route) {
              setTemporaryRouteInfo(parsedRoute.route)
            }
          }
        } catch (e) {
          window.mapToaster?.show({
            message: t("planOrder.routeToasts.fetchFailed"),
            intent: "danger",
            timeout: 3000,
          })
          console.error(e)
        } finally {
          setIsFetchingPolylines(false)
        }
      } else {
        // Branch 3: No special handling needed (passthrough changes with feature disabled, or no changes)
        await fetchNewTemporaryRoute({
          updatedRoute,
          avoidOptions: selectedAvoidOptions,
          loadType: selectedLoadType,
          carConfig,
        })
      }

      // Update driver selection if first stop changed
      if (selectedDriver) {
        const firstStop = updatedRoute.stops[0]
        if (firstStop.action !== "from_driver_location") {
          setSelectedDriver(undefined)
        }
      }
    },
    [
      fetchNewTemporaryRoute,
      getCurrentCarConfig,
      selectedAvoidOptions,
      selectedLoadType,
      routeData,
      selectedDriver,
      alternativeRoutesEnabled,
      temporaryRoute,
      alternativeRoutes,
      t,
    ],
  )

  // Update route data when it becomes available
  useEffect(() => {
    if (routeData) {
      const newTemporaryRoute = {
        stops: routeData.stops,
        jid: routeData.jid,
        lastUpdatedBy: routeData.lastUpdatedBy,
        tolls: routeData.route?.routes?.[0]?.tolls || [],
      }
      setTemporaryRoute(newTemporaryRoute)
      setTemporaryRouteInfo({
        ...routeData.route,
      })
      if (routeData.avoidOptions) {
        setSelectedAvoidOptions(routeData.avoidOptions)
      }
      if (routeData.loadType) {
        setSelectedLoadType(routeData.loadType)
      }
      if (routeData.vehicleType) {
        setSelectedVehicleType(routeData.vehicleType)
      }
      if (routeData.selectedDriver) {
        setSelectedDriver(routeData.selectedDriver)
      }

      // Set alternative routes if available from routeData
      if (alternativeRoutesEnabled && routeData.alternativeRoutes) {
        setAlternativeRoutes(routeData.alternativeRoutes)

        // Use existing per-leg alternatives if available, otherwise fetch them
        if (routeData.perLegAlternatives) {
          setPerLegAlternatives(routeData.perLegAlternatives)
        } else if (routeData.stops && routeData.stops.length >= 2) {
          // Fetch per-leg alternatives if we have enough stops
          const carConfig =
            routeData.driverCarConfig ||
            carConfigs[routeData.vehicleType || "tir"]
          handleFetchPerLegAlternatives(
            routeData.stops,
            `on-page@${window.XMPP!.options.domain}`,
            {
              avoid: routeData.avoidOptions || initialAvoidOptions || [],
              loadType: routeData.loadType || initialLoadType || [],
              carConfig,
            },
          ).then((perLegResults) => {
            setPerLegAlternatives(perLegResults)
          })
        }
      }

      setIsContentReady(true)

      if (
        hideDistanceLine !== undefined &&
        ((!sharedRouteState &&
          newTemporaryRoute.stops &&
          newTemporaryRoute.stops.length > 0) ||
          (sharedRouteState &&
            sharedRouteState.temporaryRoute?.stops?.length !==
              routeData.stops?.length))
      ) {
        setMessageText(
          generateRouteMessage({
            route: newTemporaryRoute,
            routeData,
            hideDistanceLine,
          }),
        )
      }
    }
  }, [
    routeData,
    isFailed,
    sharedRouteState,
    alternativeRoutesEnabled,
    initialAvoidOptions,
    initialLoadType,
    hideDistanceLine,
  ])

  // Tracks the route last used to generate the message, so a flag resolution
  // alone (without an actual route change) doesn't overwrite the user's edits.
  const lastGeneratedRouteRef = useRef<Route2g | undefined>(undefined)

  useEffect(() => {
    if (!temporaryRoute) {
      setMessageText("")
      lastGeneratedRouteRef.current = undefined
      return
    }

    if (hideDistanceLine === undefined) return
    if (lastGeneratedRouteRef.current === temporaryRoute) return

    // Check if sharedRouteState exists and stops are the same
    const areStopsSame =
      sharedRouteState?.temporaryRoute?.stops &&
      temporaryRoute.stops.length ===
        sharedRouteState.temporaryRoute.stops.length &&
      temporaryRoute.stops.every(
        (stop, index) =>
          stop.id === sharedRouteState.temporaryRoute.stops[index].id &&
          stop.remaining_distance ===
            sharedRouteState.temporaryRoute.stops[index].remaining_distance,
      )
    if (!areStopsSame || messageText?.length === 0) {
      lastGeneratedRouteRef.current = temporaryRoute
      setMessageText(
        generateRouteMessage({
          route: temporaryRoute,
          routeData,
          hideDistanceLine,
        }),
      )
    }
  }, [
    routeData,
    temporaryRoute,
    sharedRouteState,
    messageText,
    hideDistanceLine,
  ])

  const routeContainerRef = useRef<HTMLDivElement>(null)

  const handleAvoidSelectClose = useCallback(
    async (newAvoidOptions: AvoidType[]) => {
      const hasChanged =
        selectedAvoidOptions.length !== newAvoidOptions.length ||
        !selectedAvoidOptions.every((item) => newAvoidOptions.includes(item))
      if (!hasChanged) return
      window.analytics?.track("Anonymous_AvoidOptionsChanged", {
        avoidOptions: newAvoidOptions,
        source: "document",
      })
      setSelectedAvoidOptions(newAvoidOptions)
      if (temporaryRoute) {
        await fetchNewTemporaryRoute({
          updatedRoute: temporaryRoute,
          avoidOptions: newAvoidOptions,
          loadType: selectedLoadType,
          carConfig: getCurrentCarConfig(),
        })
      }
    },
    [
      selectedAvoidOptions,
      temporaryRoute,
      fetchNewTemporaryRoute,
      selectedLoadType,
      getCurrentCarConfig,
    ],
  )

  const handleLoadTypeSelectClose = useCallback(
    async (newLoadType: LoadType[]) => {
      const hasChanged =
        selectedLoadType.length !== newLoadType.length ||
        !selectedLoadType.every((item) => newLoadType.includes(item))
      if (!hasChanged) return
      window.analytics?.track("Anonymous_LoadTypeChanged", {
        loadType: newLoadType,
        source: "document",
      })
      setSelectedLoadType(newLoadType)
      if (temporaryRoute) {
        await fetchNewTemporaryRoute({
          updatedRoute: temporaryRoute,
          avoidOptions: selectedAvoidOptions,
          loadType: newLoadType,
          carConfig: getCurrentCarConfig(),
        })
      }
    },
    [
      fetchNewTemporaryRoute,
      selectedAvoidOptions,
      selectedLoadType,
      temporaryRoute,
      getCurrentCarConfig,
    ],
  )

  const handleVehicleTypeChange = useCallback(
    async (vehicle: VehicleType) => {
      if (selectedVehicleType === vehicle) return
      window.analytics?.track("Anonymous_VehicleTypeChanged", {
        vehicleType: vehicle,
        source: "document",
      })

      setDriverCarConfig(undefined)
      setSelectedDriver(undefined)
      setSelectedVehicleType(vehicle)
      if (temporaryRoute) {
        const updatedRoute = {
          ...temporaryRoute,
          stops: temporaryRoute?.stops?.filter(
            (stop) => stop.action !== "from_driver_location",
          ),
        }
        if (updatedRoute) {
          await fetchNewTemporaryRoute({
            updatedRoute: updatedRoute,
            avoidOptions: selectedAvoidOptions,
            loadType: selectedLoadType,
            carConfig: carConfigs[vehicle],
          })
        }
      }
    },
    [
      fetchNewTemporaryRoute,
      selectedAvoidOptions,
      selectedLoadType,
      selectedVehicleType,
      temporaryRoute,
    ],
  )

  const handleSaveRoute = useCallback(() => {
    if (temporaryRoute && temporaryRoute.stops.length > 1 && window.XMPP) {
      saveRoute(
        temporaryRoute,
        temporaryRouteInfo,
        selectedAvoidOptions || [],
        selectedLoadType || [],
        selectedVehicleType ?? "tir",
        messageText,
        {
          fileName,
          fileUrl,
          extractedData: routeData?.routeMetaData,
          driverCarConfig: isUsingDriverConfig ? driverCarConfig : undefined,
          customName: `[${isUsingDriverConfig ? "Driver" : VEHICLE_TYPE_NAMES[selectedVehicleType ?? "tir"]}] - ${generateRouteName(temporaryRoute.stops)}`,
          alternativeRoutes,
          perLegAlternatives,
        },
      )
    }
  }, [
    driverCarConfig,
    fileName,
    fileUrl,
    isUsingDriverConfig,
    messageText,
    routeData?.routeMetaData,
    selectedAvoidOptions,
    selectedLoadType,
    selectedVehicleType,
    temporaryRoute,
    temporaryRouteInfo,
    alternativeRoutes,
    perLegAlternatives,
  ])

  // Keep ref to latest save function for cleanup
  const saveRouteRef = useRef(handleSaveRoute)
  useEffect(() => {
    saveRouteRef.current = handleSaveRoute
  }, [handleSaveRoute])

  // Save route on unmount (handles sidebar navigation, tab switching, etc.)
  useEffect(() => {
    return () => {
      saveRouteRef.current()
    }
  }, [])

  // Every calculated route goes to the history right away, so routes that
  // are edited in place (rather than cleared or left) are kept as well. The
  // save waits a moment because the message text is regenerated in a later
  // render than the route itself, and typing in it should not write on every
  // keystroke.
  useEffect(() => {
    if (!temporaryRouteInfo) return
    const timer = setTimeout(() => saveRouteRef.current(), 1000)
    return () => clearTimeout(timer)
  }, [temporaryRouteInfo, messageText])

  // Handle confirming route clear
  const handleConfirmClear = useCallback(() => {
    handleSaveRoute()
    window.analytics?.track("Anonymous_RouteClearConfirmed", {
      stopCount: temporaryRoute?.stops?.length ?? 0,
      source: "document",
    })
    setSelectedDriver(undefined)
    onReset?.()
  }, [handleSaveRoute, onReset, temporaryRoute])

  const handleClose = useCallback(() => {
    handleSaveRoute()
    onClose?.(true)
  }, [handleSaveRoute, onClose])

  const handleBack = useCallback(() => {
    if (routeData) {
      if (temporaryRoute && temporaryRoute.stops && temporaryRouteInfo) {
        onBackToVerify({
          stops: temporaryRoute?.stops,
          jid: temporaryRoute?.jid,
          lastUpdatedBy: temporaryRoute?.lastUpdatedBy,
          route: temporaryRouteInfo,
          routeMetaData: routeData?.routeMetaData,
          vehicleType: selectedVehicleType,
          avoidOptions: selectedAvoidOptions,
          selectedDriver: selectedDriver,
          loadType: selectedLoadType,
          driverCarConfig: isUsingDriverConfig ? driverCarConfig : undefined,
          alternativeRoutes: alternativeRoutes,
          perLegAlternatives: perLegAlternatives,
        })
      } else {
        onClose?.(true)
      }
    }
  }, [
    onBackToVerify,
    onClose,
    routeData,
    selectedAvoidOptions,
    selectedDriver,
    selectedLoadType,
    selectedVehicleType,
    temporaryRoute,
    temporaryRouteInfo,
    alternativeRoutes,
    perLegAlternatives,
  ])

  const handleDriverSelection = useCallback(
    async (driverLocation: SelectedDriverLocation | undefined) => {
      let carConfig: CarRouteConfig | undefined = undefined
      setSelectedDriver(driverLocation)

      if (!driverLocation || !driverLocation.location) {
        setDriverCarConfig(carConfig)
        // Driver unselected - remove driver location from route if it exists
        if (temporaryRoute && temporaryRoute.stops.length > 0) {
          // Check if first stop is driver location (has special marker)
          const firstStop = temporaryRoute.stops[0]
          if (firstStop.by === selectedDriver?.driverJid) {
            // Remove driver stop and update route
            const newStops =
              temporaryRoute.stops
                .slice(1)
                .filter((stop) => stop.action !== "passthrough")
                .map((stop) => ({
                  ...stop,
                  route_handle: undefined,
                  addressUpdated: true,
                })) ?? []

            // If no stops left after removing driver, clear everything
            if (newStops.length === 0) {
              setMessageText("")
              setTemporaryRoute(undefined)
              setTemporaryRouteInfo(undefined)
              setAlternativeRoutes(undefined)
              setPerLegAlternatives(undefined)
              return
            }

            const updatedRoute = { ...temporaryRoute, stops: newStops }
            setTemporaryRoute(updatedRoute)

            // If only 1 stop left, clear route info but don't fetch
            if (newStops.length === 1) {
              setTemporaryRouteInfo(undefined)
              setAlternativeRoutes(undefined)
              setPerLegAlternatives(undefined)
              return
            }

            await fetchNewTemporaryRoute({
              updatedRoute: updatedRoute,
              avoidOptions: selectedAvoidOptions,
              loadType: selectedLoadType,
              carConfig: carConfigs["tir"],
            })
          }
        }
        return
      }

      // Driver selected - add their location as first stop
      window.analytics?.track("Anonymous_DriverSelected", {
        source: "document",
      })
      const driverLocation_converted = driverLocation.location
      if (!driverLocation_converted.lat || !driverLocation_converted.lon) {
        console.warn("Driver location missing coordinates")
        return
      }

      const driverStop: RouteStop = {
        id: `driver-${driverLocation.driverJid}`,
        driverInitials: driverLocation.driverInitials,
        by: driverLocation.driverJid,
        location: {
          ...driverLocation_converted,
          formattedAddress: `${driverLocation_converted.lat},${driverLocation_converted.lon}`,
          lat: driverLocation_converted.lat,
          lon: driverLocation_converted.lon,
        },
        action: "from_driver_location",
        is_temporary: true,
      }

      let newStops: RouteStop[]
      if (temporaryRoute && temporaryRoute.stops.length > 0) {
        // Determine which stops to keep (skip first stop if it's a driver location)
        const firstStop = temporaryRoute.stops[0]
        const shouldSkipFirst = firstStop.by // First stop is a driver location
        const existingStops = temporaryRoute.stops
          .slice(shouldSkipFirst ? 1 : 0)
          .filter((stop) => stop.action !== "passthrough")
          .map((stop) => ({
            ...stop,
            route_handle: undefined,
            addressUpdated: true,
          }))

        newStops = [driverStop, ...existingStops]
      } else {
        // No existing route, create new with driver location
        newStops = [driverStop]
      }

      const updatedRoute: Route2g = {
        jid: temporaryRoute?.jid || "on-page",
        stops: newStops,
        tolls: temporaryRoute?.tolls || [],
      }

      if (driverLocation?.driverJid) {
        carConfig = await XmppPubSubApi.fetchCarConfig(
          window.XMPP!,
          driverLocation?.driverJid,
        )
        setDriverCarConfig(carConfig)
      }

      setTemporaryRoute(updatedRoute)
      if (temporaryRoute) {
        await fetchNewTemporaryRoute({
          updatedRoute: updatedRoute,
          avoidOptions: selectedAvoidOptions,
          loadType: selectedLoadType,
          carConfig: carConfig || getCurrentCarConfig(),
        })
      }
    },
    [
      temporaryRoute,
      selectedDriver?.driverJid,
      fetchNewTemporaryRoute,
      selectedAvoidOptions,
      selectedLoadType,
      getCurrentCarConfig,
    ],
  )

  const handleDriverTextChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessageText(e.target.value)
    },
    [],
  )

  const handleLoadSavedRoute = useCallback(
    (routeId: string) => {
      handleSaveRoute()
      onLoadSavedRoute?.(routeId)
    },
    [onLoadSavedRoute, handleSaveRoute],
  )

  // Alternative routes handlers
  const handleRouteSelect = useCallback(
    (routeType: string) => {
      const selectedAlternative = alternativeRoutes?.find(
        (alt) => alt.type === routeType,
      )
      if (
        temporaryRoute &&
        temporaryRoute.stops.length > 0 &&
        selectedAlternative &&
        selectedAlternative.stops &&
        selectedAlternative.routeInfo
      ) {
        // Preserve the first stop (user's starting point) which is not included in alternatives
        const firstStop = temporaryRoute.stops[0]
        setTemporaryRoute({
          ...temporaryRoute,
          stops: [firstStop, ...selectedAlternative.stops],
          routeId: selectedAlternative.type,
        })
        setTemporaryRouteInfo(selectedAlternative.routeInfo)

        if (selectedAlternative.carRouteConfigAvoid !== undefined) {
          setSelectedAvoidOptions(selectedAlternative.carRouteConfigAvoid)
        }

        window.analytics?.track(
          window.anonymousXMPP
            ? "Anonymous_AlternativeRouteSelected"
            : "OrderOnPage_AlternativeRouteSelected",
          {
            routeType,
            distance: selectedAlternative.routeInfo.routes[0]?.distance,
            fuel: selectedAlternative.fuel,
          },
        )
      }
    },
    [alternativeRoutes, setSelectedAvoidOptions, temporaryRoute],
  )

  const handleRouteHover = useCallback((routeId: string | undefined) => {
    setHoveredRouteId(routeId)
  }, [])

  // Handle route drag state changes from MapView
  const handleRouteDragChange = useCallback((isDragging: boolean) => {
    setIsDraggingRoute(isDragging)
  }, [])

  // Calculate selected route handles (skip first stop as it's manually prepended and has no route_handle)
  const selectedRouteHandles = useMemo(
    () =>
      (temporaryRoute?.stops as RouteStopWithHandle[] | undefined)
        ?.slice(1)
        .map((stop) => stop.route_handle)
        .filter((handle) => handle !== undefined) || [],
    [temporaryRoute?.stops],
  )

  // Calculate alternatives with legs for rendering
  // Filter out alternatives whose stop count differs from clean alternatives (e.g., custom
  // routes with passthroughs have extra stops and their legs don't align with clean routes)
  const alternativesWithLegs = useMemo(() => {
    if (!alternativeRoutes) return undefined

    const canonicalStopCount = alternativeRoutes.find(
      (alt) => !alt.routeInfo.routes[0]?.isCustom,
    )?.stops.length

    return alternativeRoutes
      .filter(
        (alt) =>
          canonicalStopCount === undefined ||
          alt.stops.length === canonicalStopCount,
      )
      .map((alternativeRoute, index) => ({
        type: alternativeRoute.type,
        uniqueId: `${alternativeRoute.type}-${index}`,
        legs: alternativeRoute.stops.map((stop) => ({
          stop,
          route_handle: stop.route_handle,
          polyline: stop.polyline,
        })),
      }))
  }, [alternativeRoutes])

  // Calculate per-leg alternatives for map rendering
  const perLegAlternativesForMap = useMemo(
    () =>
      perLegAlternatives?.flatMap((legGroup) => {
        return legGroup.alternatives.map((alternative, altIndex) => ({
          route_handle: alternative.stops[0]?.route_handle,
          type: alternative.type,
          uniqueId: `leg-${legGroup.fromStopIndex}-${legGroup.toStopIndex}-${alternative.type}-${altIndex}`,
          fromStopIndex: legGroup.fromStopIndex,
          toStopIndex: legGroup.toStopIndex,
          tolls: alternative.tolls,
          fuel: alternative.fuel,
          legs: alternative.stops.map((stop) => ({
            stop,
            route_handle: stop.route_handle,
            polyline: stop.polyline,
          })),
        }))
      }),
    [perLegAlternatives],
  )

  // Calculate unique alternative legs for rendering
  const uniqueAlternativeLegs = useMemo(() => {
    const allLegs: Array<{
      leg: { stop: RouteStopWithHandle; route_handle: string; polyline: string }
      legIndex: number
      id: string
      routeId: string
      tolls: Toll[]
      fuel: number
    }> = []

    // Collect legs from whole-route alternatives
    if (alternativesWithLegs && alternativesWithLegs.length > 0) {
      alternativesWithLegs.forEach((alternative) => {
        alternative.legs.forEach((leg, legIndex) => {
          if (!selectedRouteHandles.includes(leg.route_handle)) {
            allLegs.push({
              leg,
              legIndex,
              id: leg.route_handle,
              routeId: alternative.type,
              tolls: [],
              fuel: 0,
            })
          }
        })
      })
    }

    // Collect legs from per-leg alternatives
    if (perLegAlternativesForMap) {
      perLegAlternativesForMap.forEach((legAlt) => {
        legAlt.legs.forEach((leg, legIndex) => {
          if (!selectedRouteHandles.includes(leg.route_handle)) {
            allLegs.push({
              leg,
              legIndex: legAlt.fromStopIndex ?? legIndex,
              id: leg.route_handle,
              routeId: legAlt.uniqueId,
              tolls: [],
              fuel: 0,
            })
          }
        })
      })
    }

    // Deduplicate by route_handle
    const seenHandles = new Set<string>()
    return allLegs.filter((legData) => {
      if (seenHandles.has(legData.leg.route_handle)) {
        return false
      }
      seenHandles.add(legData.leg.route_handle)
      return true
    })
  }, [alternativesWithLegs, perLegAlternativesForMap, selectedRouteHandles])

  // Determine which legs have passthrough stops (hide alternatives for these legs)
  const legsWithPassthroughs = useMemo(() => {
    if (!temporaryRoute?.stops || temporaryRoute.stops.length < 2)
      return new Set<number>()

    const stops = temporaryRoute.stops
    const result = new Set<number>()

    const nonPassthroughStopIndices: number[] = []
    stops.forEach((stop, index) => {
      if (stop.action !== "passthrough") {
        nonPassthroughStopIndices.push(index)
      }
    })

    for (let leg = 0; leg < nonPassthroughStopIndices.length - 1; leg++) {
      const fromIdx = nonPassthroughStopIndices[leg]
      const toIdx = nonPassthroughStopIndices[leg + 1]
      if (toIdx - fromIdx > 1) {
        result.add(leg)
      }
    }

    return result
  }, [temporaryRoute?.stops])

  // Handle clicking on alternative leg to create custom route
  const handleLegClick = useCallback(
    async (route_handle: string, legIndex: number) => {
      if (!temporaryRoute || !alternativeRoutes) {
        return
      }

      // Find the leg by route_handle
      const leg = uniqueAlternativeLegs.find(
        (legData) => legData.leg.route_handle === route_handle,
      )

      if (!leg) {
        console.error("Leg not found for route_handle:", route_handle)
        return
      }

      const currentStops = temporaryRoute.stops as RouteStopWithHandle[]
      const firstStop = currentStops[0]

      // The remaining stops (excluding first stop)
      const remainingStops = currentStops.slice(1)

      // Find actual indices of non-passthrough stops in remainingStops
      const nonPassthroughIndices: number[] = []
      remainingStops.forEach((stop, index) => {
        if (stop.action !== "passthrough") {
          nonPassthroughIndices.push(index)
        }
      })

      // legIndex is the logical (non-passthrough) stop index within the alternatives
      const actualStopIndex = nonPassthroughIndices[legIndex]

      if (actualStopIndex === undefined) {
        console.error(
          "Invalid leg index:",
          legIndex,
          "non-passthrough stops count:",
          nonPassthroughIndices.length,
        )
        return
      }

      // Find the previous non-passthrough stop (START of the segment)
      const prevStopLogicalIndex = legIndex - 1
      const prevStopActualIndex =
        prevStopLogicalIndex >= 0
          ? nonPassthroughIndices[prevStopLogicalIndex]
          : -1

      // Build new stops (excluding first stop):
      // - Keep everything up to and including the previous non-passthrough stop
      // - Add the new leg's stop (replacing the current stop and any passthroughs before it)
      // - Keep everything after the current stop
      const newRemainingStops: RouteStopWithHandle[] = [
        ...remainingStops.slice(0, prevStopActualIndex + 1),
        leg.leg.stop,
        ...remainingStops.slice(actualStopIndex + 1),
      ]

      const carConfig = getCurrentCarConfig()

      setIsFetchingPolylines(true)
      try {
        const result = await AdHocCommands.getRoutePlan(
          window.XMPP!,
          `on-page@${window.XMPP!.options.domain}`,
          [firstStop, ...newRemainingStops],
          {
            ...carConfig,
            avoid: selectedAvoidOptions,
            loadType: selectedLoadType,
          },
        )

        const customRoad = PlanRouteParser.parse(result)

        if (!customRoad?.alternativeRoutes?.[0]) {
          return
        }

        // Create custom route info
        const customRouteInfo: RouteInfo = {
          routes: [
            {
              ...(customRoad.alternativeRoutes[0].routeInfo.routes[0] || {}),
              id: "Custom",
              label: "Custom",
              isCustom: true,
            },
          ],
        }

        const customAlternativeRoute: AlternativeRoute = {
          type: "Custom",
          stops: customRoad.stops as RouteStopWithHandle[],
          routeInfo: customRouteInfo,
          tolls: customRoad.alternativeRoutes[0].tolls,
          fuel: customRoad.alternativeRoutes[0].fuel,
        }

        // Remove any existing Custom route and add the new one
        const nonCustomAlternatives = alternativeRoutes.filter(
          (alt) => !alt.routeInfo.routes[0].isCustom,
        )

        // Add custom route to alternatives array
        setAlternativeRoutes([customAlternativeRoute, ...nonCustomAlternatives])

        // Set as selected route - preserve first stop
        setTemporaryRoute({
          ...temporaryRoute,
          stops: [firstStop, ...customRoad.stops],
          routeId: "Custom",
          tolls: customRoad.alternativeRoutes[0].tolls,
        })
        setTemporaryRouteInfo(customRouteInfo)

        // Track alternative leg selection
        window.analytics?.track(
          window.anonymousXMPP
            ? "Anonymous_AlternativeLegSelected"
            : "OrderOnPage_AlternativeLegSelected",
          {
            legIndex,
          },
        )
      } catch (err) {
        console.error(`Error fetching custom route:`, err)
      } finally {
        setIsFetchingPolylines(false)
      }
    },
    [
      temporaryRoute,
      alternativeRoutes,
      uniqueAlternativeLegs,
      getCurrentCarConfig,
      selectedAvoidOptions,
      selectedLoadType,
    ],
  )

  const selectedRouteId = temporaryRouteInfo?.routes?.[0]?.id
  const hoverRouteVisible = Boolean(
    hoveredRouteId && hoveredRouteId !== selectedRouteId && !isDraggingRoute,
  )

  // Hide alternatives when dragging route to add passthrough
  const showAlternatives = alternativeRoutesEnabled && !isDraggingRoute

  const routingProvider = useSelector(selectRoutingProvider)
  const isHereRouting = useSelector(selectIsHereRouting)
  const summaryText = getOptionsSummaryText({
    stops: temporaryRoute?.stops,
    avoid: selectedAvoidOptions,
    loadType: selectedLoadType,
    routingProvider,
  })

  const showClearRouteButton =
    temporaryRoute ||
    selectedAvoidOptions.length > 0 ||
    selectedLoadType.length > 0 ||
    selectedVehicleType !== "tir"

  const inProduct = Boolean(window.XMPP && !window.anonymousXMPP)
  const hasSidebar = Boolean(window.XMPP)

  return (
    <APIProvider apiKey={import.meta.env.REACT_APP_GOOGLE_MAP_KEY}>
      <div
        className={`${hasSidebar ? "left-[49px] w-[calc(100vw-48px)]" : ""} fixed inset-0 z-[100] h-screen bg-[var(--panel-color)] ${isHidden ? "hidden" : ""}`}
      >
        <ResizablePanelGroup
          direction="horizontal"
          className="max-w-screen min-h-screen"
        >
          <ResizablePanel
            className="relative flex flex-col bg-[var(--panel-color)]"
            defaultSize={35}
            maxSize={50}
            minSize={25}
          >
            {isFetchingPolylines && (
              <div className="absolute inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
                <div className="flex flex-col items-center space-y-4">
                  <Spinner size={25} />
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {t("planOrder.planner.fetchingRoute")}
                  </p>
                </div>
              </div>
            )}
            <div className="flex w-full flex-col p-4 pb-2">
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  onClick={handleBack}
                  className="self-start pl-0 text-sm"
                  disabled={!isContentReady}
                >
                  <ArrowLeft className="h-4 w-4" />
                  {routeData &&
                  temporaryRoute &&
                  temporaryRoute.stops &&
                  temporaryRouteInfo
                    ? t("common.back")
                    : t("common.close")}
                </Button>

                {/* Saved routes selector and clear route button */}
                <div className="flex items-center gap-2">
                  {showClearRouteButton && (
                    <ClearRouteDialog
                      onConfirm={handleConfirmClear}
                      disabled={isFetchingPolylines}
                    />
                  )}
                  <SavedRoutesSelector
                    onLoadSavedRoute={handleLoadSavedRoute}
                    disabled={isFetchingPolylines}
                  />
                </div>
              </div>
              <h2 className="mb-1 mt-4 font-semibold text-muted-foreground">
                {t("planOrder.planner.step2")}
              </h2>
              <h1 className="text-2xl font-bold">
                {t("planOrder.planner.title")}
              </h1>
            </div>
            <div
              ref={routeContainerRef}
              className="relative z-10 flex w-full flex-1 flex-col overflow-hidden bg-[var(--panel-color)]"
            >
              {!isLoading && isFailed ? (
                <div className="flex w-full flex-col p-4">
                  <Alert variant="destructive">
                    <AlertCircleIcon />
                    <AlertTitle>
                      {t("planOrder.planner.processFailed.title")}
                    </AlertTitle>
                    <AlertDescription>
                      <p>{t("planOrder.planner.processFailed.description")}</p>
                      <ul className="list-inside list-disc text-sm">
                        <li>
                          {t("planOrder.planner.processFailed.checkValid")}
                        </li>
                        <li>
                          {t("planOrder.planner.processFailed.checkFormat")}
                        </li>
                      </ul>
                    </AlertDescription>
                  </Alert>
                </div>
              ) : null}
              <div className="flex h-full w-full flex-col gap-2">
                <div className="w-full overflow-x-auto overscroll-x-contain">
                  <div className="flex w-max min-w-full gap-2 px-4 py-2">
                    <div className="flex-shrink-0">
                      <VehicleTypeSelect
                        key={
                          isUsingDriverConfig ? "from-driver" : "manual-select"
                        }
                        value={
                          isUsingDriverConfig ? undefined : selectedVehicleType
                        }
                        onValueChange={handleVehicleTypeChange}
                        disabled={haveFirstStopAsDriverLocation}
                        placeholder={
                          isUsingDriverConfig
                            ? t("planOrder.planner.driverCar")
                            : undefined
                        }
                      />
                    </div>
                    <div className="flex-shrink-0">
                      <AvoidSelect
                        onClose={handleAvoidSelectClose}
                        initialSelected={selectedAvoidOptions}
                      />
                    </div>
                    <div className="flex-shrink-0">
                      <LoadTypeSelect
                        onClose={handleLoadTypeSelectClose}
                        initialSelected={selectedLoadType}
                      />
                    </div>
                    {inProduct && (
                      <div className="w-48 shrink-0">
                        <DriverSelect
                          value={selectedDriver?.driverJid}
                          onValueChange={handleDriverSelection}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Route options summary */}
                {summaryText && (
                  <div className="px-4">
                    <div className="rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-600 dark:bg-blue-900/20">
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        {summaryText}
                      </p>
                    </div>
                  </div>
                )}

                {/* Vehicle type info */}
                <div className="flex w-full gap-2 px-4 *:flex-1">
                  <CollapsibleVehicleTollInfo
                    config={getCurrentCarConfig()}
                    tolls={temporaryRouteInfo?.routes?.[0]?.tolls}
                    showFuel={isHereRouting}
                  />
                </div>

                <div className="flex min-h-0 flex-1 flex-col">
                  <ScrollAreaWithShadow className="flex-1 px-4">
                    <div className="w-full overflow-x-auto overscroll-x-contain">
                      <div className="w-max min-w-full space-y-4">
                        <RouteStopList
                          driverJid="on-page"
                          isReadOnlyMap={false}
                          isAnalyzeOrderMap
                          routeFromAnalyzeOrder={temporaryRoute}
                          onTemporaryRouteUpdate={handleTemporaryRouteUpdate}
                          temporaryAvoidOptions={selectedAvoidOptions}
                        />
                      </div>
                    </div>
                  </ScrollAreaWithShadow>

                  {/* Message input area - fixed to bottom */}
                  <div className="w-full border-t bg-[var(--panel-color)] p-4">
                    <div className="flex w-full flex-col gap-2">
                      {naviTransportId && (
                        <div className="rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:bg-blue-950 dark:text-blue-200">
                          {routeData?.routeMetaData?.order_number
                            ? t("planOrder.planner.marcosOrderWithReference", {
                                id: naviTransportId,
                                reference: routeData.routeMetaData.order_number,
                              })
                            : t("planOrder.planner.marcosOrder", {
                                id: naviTransportId,
                              })}
                        </div>
                      )}
                      {isMarcosMode && (
                        <div className="rounded-lg border border-blue-200 bg-blue-50 px-5 py-4 text-center text-lg font-semibold leading-snug text-blue-900 dark:border-blue-700 dark:bg-blue-950 dark:text-blue-200">
                          {t("planOrder.planner.marcosHint")}
                        </div>
                      )}
                      <div className={cn("flex-grow")}>
                        <Textarea
                          ref={textareaRef}
                          value={messageText}
                          onChange={handleDriverTextChange}
                          placeholder={t(
                            "planOrder.planner.messagePlaceholder",
                          )}
                          className={cn(
                            "font-inherit max-h-[300px] min-h-[25vh] resize-none md:text-sm",
                          )}
                        />
                      </div>

                      <div className="flex items-center gap-2 overflow-x-auto overscroll-x-contain">
                        <Button variant="secondary" onClick={handleClose}>
                          {t("common.close")}
                        </Button>
                        <div className="flex-1" />
                        {!isMarcosMode && (
                          <Button
                            disabled={!temporaryRoute || isGeneratingShareLink}
                            variant="outline"
                            onClick={shareRoute}
                          >
                            {isGeneratingShareLink ? (
                              <Spinner size={16} className="mr-2" />
                            ) : shareSuccessAnimate ? (
                              <Check className="mr-2 h-4 w-4 duration-200 animate-in zoom-in" />
                            ) : (
                              <Share2 className="mr-2 h-4 w-4 transition-transform hover:scale-110" />
                            )}
                            {t("planOrder.planner.shareRoute")}
                          </Button>
                        )}
                        <Button
                          disabled={!temporaryRoute}
                          className="bg-blue-600 px-6 text-white hover:bg-blue-700"
                          onClick={copyMessage}
                        >
                          {copySuccessAnimate ? (
                            <Check className="mr-2 h-4 w-4 duration-200 animate-in zoom-in" />
                          ) : (
                            <Copy className="mr-2 h-4 w-4 transition-transform hover:scale-110" />
                          )}{" "}
                          {t("planOrder.planner.copyMessage")}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ResizablePanel>
          <ResizableHandle withHandle className="z-[1001]" />
          <ResizablePanel defaultSize={75}>
            <div className="relative z-[1000] flex h-full items-center justify-center">
              <MapView
                isAnalyzeOrderMap
                temporaryRoute={isContentReady ? temporaryRoute : undefined}
                temporaryRouteInfo={
                  isContentReady ? temporaryRouteInfo : undefined
                }
                temporaryAvoidOptions={
                  isContentReady ? selectedAvoidOptions : undefined
                }
                temporaryLoadType={
                  isContentReady ? selectedLoadType : undefined
                }
                temporaryCarConfig={getCurrentCarConfig()}
                onTemporaryRouteUpdate={handleTemporaryRouteUpdate}
                onRouteDragChange={handleRouteDragChange}
                driverJid="on-page"
                isFullScreen
                isReadOnlyMap={!isContentReady}
              >
                {/* Show single stop marker when there's only one stop and no route yet */}
                {isContentReady &&
                  temporaryRoute?.stops?.length === 1 &&
                  !temporaryRouteInfo && (
                    <SingleStopMarker stop={temporaryRoute.stops[0]} />
                  )}
                {/* Render unique alternative legs - only when feature is enabled and not dragging */}
                {showAlternatives &&
                  uniqueAlternativeLegs
                    .filter(
                      (legData) => !legsWithPassthroughs.has(legData.legIndex),
                    )
                    .map((legData) => (
                      <AlternativeLeg
                        key={legData.id}
                        leg={legData.leg}
                        legIndex={legData.legIndex}
                        id={legData.id}
                        visible={true}
                        hoverRouteVisible={hoverRouteVisible}
                        onLegClick={handleLegClick}
                        routeLabel={legData.routeId}
                      />
                    ))}
                {showAlternatives && hoverRouteVisible && hoveredRouteId && (
                  <HoveredRouteHighlight
                    routeId={hoveredRouteId}
                    alternativeRoutes={alternativeRoutes}
                  />
                )}
                {/* Alternative Route Selector - only when feature is enabled and not dragging */}
                {showAlternatives && (
                  <AlternativeRouteSelector
                    selectedRouteId={temporaryRouteInfo?.routes?.[0]?.id}
                    alternativeRoutes={alternativeRoutes}
                    onRouteSelect={handleRouteSelect}
                    onRouteHover={handleRouteHover}
                    hoveredRouteId={hoveredRouteId}
                    className="mb-4"
                    disabled={isFetchingPolylines}
                    showFuel={isHereRouting}
                  />
                )}
              </MapView>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </APIProvider>
  )
}

export default AnalyzeOrderOnPageMap
